import { useState } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';

import { handleError } from '../utils/utils';

import {
    Banner,
    BannerTranslationsType,
    CreateOrUpdateBanner,
} from '../types/bannerTypes';

type BasepathOption = {
    basepath?: string;
};

export function useBanners(options: BasepathOption = {}) {
    const { basepath } = options;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [banners, setBanners] = useState<Banner[]>([]);

    async function fetch() {
        const url = basepath ? `${basepath}/banners/v2` : `/api/banners/v2`;

        setError(null);
        setIsLoading(true);

        try {
            const { data } = await axios.get<Banner[]>(url);

            setBanners(data);
            setIsLoading(false);
        } catch (error: unknown) {
            setError(error as AxiosError);
            setIsLoading(false);
        }
    }

    return {
        banners,
        isLoading,
        errorMessage: error
            ? handleError('Fetch banners error', error, false)
            : undefined,
        fetch,
    };
}

export function useBanner(bannerId: string, options: BasepathOption = {}) {
    const { basepath } = options;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [banner, setBanner] = useState<Banner | null>(null);

    async function fetch() {
        const url = basepath
            ? `${basepath}/banners/v2/${bannerId}`
            : `/api/banners/v2/${bannerId}`;

        setError(null);
        setIsLoading(true);

        try {
            const { data } = await axios.get<Banner>(url);

            setBanner(data);
            setIsLoading(false);
        } catch (error: unknown) {
            setError(error as AxiosError);
            setIsLoading(false);
        }
    }

    return {
        banner,
        isLoading,
        errorMessage: error
            ? handleError('Fetch banner error', error, false)
            : undefined,
        fetch,
    };
}

export function useCreateBanner(options: BasepathOption = {}) {
    const { basepath } = options;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [id, setId] = useState<number | null>(null);

    async function createBanner(body: CreateOrUpdateBanner) {
        const url = basepath ? `${basepath}/banners/v2` : `/api/banners/v2`;

        setError(null);
        setIsLoading(true);

        try {
            const { data } = await axios.post<{ id: number }>(url, body);

            setId(data.id);
            setIsLoading(false);
        } catch (error: unknown) {
            setError(error as AxiosError);
            setIsLoading(false);
            // Need to throw an exception to make Promise to be rejected
            throw error;
        }
    }

    return {
        bannerId: id,
        isLoading,
        errorMessage: error
            ? handleError('Create banner error', error, true)
            : undefined,
        createBanner,
    };
}

export function useUpdateBanner(
    bannerId: string,
    options: BasepathOption = {},
) {
    const { basepath } = options;
    const [updatedBanner, setUpdatedBanner] = useState<Banner | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    async function updateBanner(body: CreateOrUpdateBanner) {
        const url = basepath
            ? `${basepath}/banners/v2/${bannerId}`
            : `/api/banners/v2/${bannerId}`;

        setError(null);
        setIsLoading(true);

        try {
            const { data } = await axios.put<Banner>(url, body);
            if (data?.id) {
                setUpdatedBanner(data);
            }
            setIsLoading(false);
        } catch (error: unknown) {
            setError(error as AxiosError);
            setIsLoading(false);
            // Need to throw an exception to make Promise to be rejected
            throw error;
        }
    }

    return {
        updatedBanner: updatedBanner ?? null,
        isLoading,
        errorMessage: error
            ? handleError('Update banner error', error, true)
            : undefined,
        updateBanner,
    };
}

export function useBannerTranslation(stringId: string | null) {
    const [translations, setTranslations] =
        useState<BannerTranslationsType | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    async function getBannerTranslations() {
        try {
            if (stringId !== null) {
                setIsLoading(true);
                setErrorMessage(undefined);

                const { data }: AxiosResponse<BannerTranslationsType> =
                    await axios.get(`/api/banners/v2/${stringId}/translations`);

                if (_.isEmpty(data.title) && _.isEmpty(data.subtitle)) {
                    throw new Error(
                        'There are no translations for this string ID.',
                    );
                }
                setTranslations(data);
            }
        } catch (error) {
            const errorMessage = handleError(
                'Could not load banner translation from the spreadsheet',
                error,
                false,
            );
            setTranslations(null);
            setErrorMessage(errorMessage);
        }
        setIsLoading(false);
    }

    return {
        translations,
        isLoading,
        errorMessage,
        getBannerTranslations,
    };
}
